import { useTheme } from '@mui/material/styles';
import { Box, CardContent, Stack, Typography } from '@mui/material';
import { StyledCard, cardContent, cardFooter, cardHeader } from './style';
import { AlarmCheckbox } from 'src/components/minimals/checkbox/AlarmCheckbox';
import { formatUTCDateToLocal } from 'src/utilities';
import { EventStatus } from 'src/features/alarm-pop-up/types';
import { renderTitleIcon } from 'src/features/alarm-pop-up/utils';
import { formatAcknowledgedBy } from 'src/layouts/alarmMobileLayout/formatAcknowledgedBy';
import { AlarmDataV3 } from 'src/types/alarms';
import { MobileAlarmPopUp } from 'src/features/alarm-pop-up/components/mobile/MobileAlarmPopUp';
import { DesktopAlarmPopUp } from 'src/features/alarm-pop-up/components/desktop/DesktopAlarmPopUp';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetAlarmPopupInfo } from 'src/hooks/apiHooks/useGetAlarmPopupInfo';
import { useGetAlarmPopupNotifications } from 'src/hooks/apiHooks/useGetAlarmPopupNotifications';
import { HandleDeleteAlarmEventTypes } from 'src/features/alarms/types';
import { useState, useEffect } from 'react';
import { ACCESS_ROLES } from 'src/config';
import useQueryParamsActions from 'src/hooks/useQueryParamsActions';
import { useResponsive } from 'src/hooks/useResponsive';

type MobileAlarmCardProps = {
  itemRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  index: number;
  logs?: boolean;
  alarmData: AlarmDataV3;
  handleDeleteAlarmEvent: ({ customerId, siteId, alarmId }: HandleDeleteAlarmEventTypes) => void;
  setAcknowledgedId?: any;
  setRefreshTrigger?: any;
  setAlarmData: any;
  onAcknowledged: any;
};

export const MobileAlarmCard = ({
  alarmData,
  itemRefs,
  logs,
  index,
  handleDeleteAlarmEvent,
  setAcknowledgedId,
  setRefreshTrigger,
  setAlarmData,
  onAcknowledged,
}: MobileAlarmCardProps) => {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');
  const {
    customerId: { value: customerId },
    getCurrentRole,
  } = useAuthentication();

  const [isOpenPopup, setIsOpenPopup] = useState(false);

  const { get } = useQueryParamsActions();

  const eventId = get('eventId');

  useEffect(() => {
    if (eventId && eventId === alarmData.id) {
      setIsOpenPopup(true);
    }
  }, [eventId, alarmData.id]);

  const onClosePopup = () => {
    setIsOpenPopup(false);
  };

  const role = getCurrentRole(customerId, null);

  const isDeleteAlarmEventAllowed = role === ACCESS_ROLES.SUPER_ADMIN;

  const { alarmPopupInfo } = useGetAlarmPopupInfo({
    customerId: customerId!,
    siteId: alarmData.siteId,
    eventId: alarmData.id,
    isOpen: isOpenPopup,
  });
  const { notifications } = useGetAlarmPopupNotifications({
    customerId: customerId!,
    eventId: alarmData.id,
    isOpen: isOpenPopup,
  });

  const getLabelStatusIcon = () => {
    if (alarmData.status === EventStatus.CLEARED) {
      return renderTitleIcon(alarmData, theme, theme.palette.grey[300]);
    } else {
      return renderTitleIcon(alarmData, theme);
    }
  };

  return (
    <StyledCard
      logs={logs}
      ref={(el) => (itemRefs.current[index] = el)}
      theme={theme}
      onClick={() => {
        if (!isOpenPopup) {
          setIsOpenPopup(true);
        }
      }}
      data-sm={`alarm-card-${alarmData.title}`}
    >
      <CardContent sx={cardContent} data-sm={`alarm-card-wrapper-${alarmData.title}`}>
        <Box sx={cardHeader}>
          <Box>
            <Typography variant="subtitle1">{alarmData?.title}</Typography>
            <Typography variant="body2">{alarmData?.mainTag?.name}</Typography>
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            data-sm={`alarm-${alarmData?.status}-${alarmData?.priority?.toLocaleLowerCase()}`}
          >
            {getLabelStatusIcon()}
          </Box>
        </Box>
        <Box sx={cardFooter}>
          <Stack flexDirection={'row'}>
            <Stack>
              <span style={{ display: 'inline' }}>Site: </span>
              <span style={{ display: 'inline' }}>Start: </span>
              <span style={{ display: 'inline' }}>End: </span>
            </Stack>
            <Stack sx={{ ml: '10px' }} data-sm="alarm-data-information">
              <Typography variant="subtitle1" component="span">
                {alarmData?.siteName}
              </Typography>
              <Typography variant="subtitle1" component="span">
                {alarmData?.startDate ? formatUTCDateToLocal(false, alarmData?.startDate) : '-'}
              </Typography>
              <Typography variant="subtitle1" component="span">
                {alarmData?.endDate ? formatUTCDateToLocal(false, alarmData?.endDate) : '-'}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <Box data-sm="ack-box">
          <AlarmCheckbox acknowledgedBy={formatAcknowledgedBy(alarmData?.acknowledgement)} />

          <Typography
            data-sm="ack-information"
            variant="body2"
            component="span"
            sx={{ fontWeight: 'bold' }}
          >
            {formatAcknowledgedBy(alarmData?.acknowledgement) || 'Unacknowledged'}
          </Typography>
        </Box>
      </CardContent>
      {isMobile ? (
        <MobileAlarmPopUp
          open={isOpenPopup}
          onClose={onClosePopup}
          customerId={customerId!}
          siteId={alarmData?.siteId}
          alarmData={alarmPopupInfo}
          notifications={notifications}
          onAcknowledgedChange={setAcknowledgedId}
          handleDeleteAlarmEvent={handleDeleteAlarmEvent}
          isDeleteAlarmEventAllowed={isDeleteAlarmEventAllowed}
          setRefreshTrigger={setRefreshTrigger}
          onAcknowledged={onAcknowledged}
        />
      ) : (
        <DesktopAlarmPopUp
          open={isOpenPopup}
          onClose={onClosePopup}
          customerId={customerId!}
          siteId={alarmData?.siteId}
          alarmData={alarmPopupInfo}
          notifications={notifications}
          onAcknowledgedChange={setAcknowledgedId}
          handleDeleteAlarmEvent={handleDeleteAlarmEvent}
          isDeleteAlarmEventAllowed={isDeleteAlarmEventAllowed}
          setRefreshTrigger={setRefreshTrigger}
          setAlarmData={setAlarmData}
          onAcknowledged={onAcknowledged}
        />
      )}
    </StyledCard>
  );
};
